import { useQuery } from '@tanstack/react-query'
import {
  Alert,
  Chip,
  CircularProgress,
  Dialog, DialogContent, DialogTitle,
  IconButton, Stack,
  Table, TableBody, TableCell, TableHead, TableRow,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

import { governanceApi } from '../services/governance-api'

interface IProps {
  workspaceId: string
  open: boolean
  category: 'cargo' | 'regiao_lotacao'
  /** Bucket count from the parent's funnel data, used to surface truncation when rows.length < total. */
  total?: number
  onClose(): void
}

const PAGE_LIMIT = 200

const CATEGORY_TITLES: Record<IProps['category'], string> = {
  cargo: 'Perdas por cargo (K9a)',
  regiao_lotacao: 'Perdas por região / lotação (K9b)',
}

const ADDRESSABLE_TYPE_LABELS: Record<string, string> = {
  ROLE: 'Filtro por cargo',
  REGION: 'Filtro por região',
  LOTACAO: 'Filtro por lotação',
}

/**
 * K9a (cargo) / K9b (regiao_lotacao) drill-down dialog. Pattern B — full PII.
 * Lists beneficiários present in PEOPLE but absent from latest MASSA_ENDERECAVEL.
 * Columns shown adapt to the workspace's addressableType (returned in the response).
 */
export function AddressabilityLossesDrilldownDialog({ workspaceId, open, category, total, onClose }: IProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['governance', 'addressability-losses-drilldown', workspaceId, category],
    queryFn: () => governanceApi.addressabilityLossesDrilldown({ workspaceId, category, limit: PAGE_LIMIT }),
    enabled: open && !!workspaceId,
  })

  const rows = data?.data.rows ?? []
  const addressableType = data?.data.addressable_type ?? ''
  const showPosition = addressableType === 'ROLE'
  const showState = addressableType === 'REGION'
  const showLotation = addressableType === 'LOTACAO'

  const isTruncated =
    typeof total === 'number' ? rows.length < total : rows.length === PAGE_LIMIT

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6" component="div">{CATEGORY_TITLES[category]}</Typography>
          {ADDRESSABLE_TYPE_LABELS[addressableType] && (
            <Chip size="small" label={ADDRESSABLE_TYPE_LABELS[addressableType]} variant="outlined" />
          )}
        </Stack>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && <CircularProgress size={20} />}
        {isError && <Typography color="error">Erro ao carregar perdas.</Typography>}
        {data && (
          <>
            {isTruncated && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {typeof total === 'number'
                  ? `Exibindo ${rows.length} de ${total.toLocaleString('pt-BR')} beneficiários. Refine os filtros para ver mais.`
                  : `Exibindo os primeiros ${PAGE_LIMIT} beneficiários. Pode haver mais resultados não exibidos.`}
              </Alert>
            )}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>CPF</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Titular</TableCell>
                  {showPosition && <TableCell>Cargo</TableCell>}
                  {showState && <TableCell>UF</TableCell>}
                  {showLotation && <TableCell>Lotação</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={`${r.person_tax_payer_id}-${i}`}>
                    <TableCell>{r.person_tax_payer_id}</TableCell>
                    <TableCell>{r.person_name ?? '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={r.person_type === 'employee' ? 'Titular' : 'Dependente'} variant="outlined" />
                    </TableCell>
                    <TableCell>{r.person_type === 'dependent' ? (r.employee_name ?? '—') : '—'}</TableCell>
                    {showPosition && <TableCell>{r.employee_position ?? '—'}</TableCell>}
                    {showState && <TableCell>{r.employee_state ?? '—'}</TableCell>}
                    {showLotation && <TableCell>{r.employee_lotation ?? '—'}</TableCell>}
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow>
                    {/* colSpan large enough for any future column count — header is the source of truth. */}
                    <TableCell colSpan={99} align="center">
                      Nenhum beneficiário excluído por este filtro.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
